import { type ReactNode, useEffect, useRef } from 'react'
import type { Message } from '../api/types'
import { Composer } from '../composer/Composer'
import { openAt } from '../read/openAt'
import { useSeen } from '../read/useSeen'
import { threadReach, threadsOf } from './format'
import { PINNED_WITHIN, Stream, type StreamProps } from './Stream'

type ThreadProps = Omit<StreamProps, 'newLineBefore' | 'selectedId' | 'threadId' | 'openThreadId'> & {
  unread: ReadonlySet<string>
  /** Given what the thread has shown long enough to be read. */
  onSeen: (ids: string[]) => void
  root: Message
  room: string
  /** A phone's way back to the room, which gives the thread a header of its own; a desktop's column has one. */
  back?: ReactNode
  narrow: boolean
  onSend: (body: string, alsoInRoom: boolean) => Promise<void>
}

/**
 * One thread: its first message in full, its replies in the one message row,
 * and the box that answers it. A desktop holds it in the right column, a phone
 * gives it the screen.
 */
export function Thread({ root, room, back, narrow, onSend, onSeen, ...stream }: ThreadProps) {
  const box = useRef<HTMLDivElement>(null)
  const phone = back !== undefined
  // Opened to answer it. A phone keeps its keyboard down until the box is tapped.
  useEffect(() => {
    if (!phone) box.current?.querySelector('textarea')?.focus()
  }, [phone])
  const replies = threadsOf(stream.messages).get(root.id) ?? []
  // As the room does: it opens at the first unread reply, or else at the
  // newest, and follows new ones only from the bottom.
  const log = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  useEffect(() => {
    const node = log.current
    const first = replies.find((reply) => stream.unread.has(reply.id))
    if (node) pinned.current = openAt(node, first ? node.querySelector(`[data-message-id="${first.id}"]`) : null)
  }, [])
  useEffect(() => {
    const node = log.current
    if (node && pinned.current) node.scrollTop = node.scrollHeight
  }, [replies.length])
  // Open is what reads a thread's replies, as they are seen here.
  useSeen(log, stream.unread, onSeen)

  return (
    <div style={{ flex: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {phone && (
        <header
          style={{
            display: 'flex', alignItems: 'center', gap: 10, flex: 'none',
            padding: 'calc(10px + var(--safe-top)) 16px 11px', borderBottom: '1px solid var(--border)',
          }}
        >
          {back}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, font: '600 16px var(--font-interface)' }}>Thread</h1>
            <span style={{ fontSize: 12.5, color: 'var(--text-meta)' }}># {room}</span>
          </div>
        </header>
      )}
      <div
        ref={log}
        role="log"
        aria-label="Replies"
        onScroll={(event) => {
          const node = event.currentTarget
          pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight < PINNED_WITHIN
        }}
        className="sp-scroller"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain', padding: '4px 18px 12px' }}
      >
        {/* The room's page starts after this thread did, so it opens on the oldest message here. */}
        {root.reply_to && (
          <p style={{ margin: 0, padding: '10px 0 2px', font: '400 12px var(--font-interface)', color: 'var(--text-meta)' }}>
            Earlier replies are not loaded
          </p>
        )}
        <Stream {...stream} threadId={root.id} selectedId={null} />
      </div>
      <div ref={box} style={{ flex: 'none' }}>
        <Composer
          roster={stream.roster}
          nobodyRunning={false}
          narrow={narrow}
          onSend={onSend}
          thread={{ room, reaches: threadReach(stream.messages, root, replies, stream.myName) }}
        />
      </div>
    </div>
  )
}
